import { ReactElement, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import useOverwolfInterop from "@/hooks/useOverwolfInterop";
import useOnAuthThirdweb from "@/hooks/useOnAuthThirdweb";
import { addError } from "@/redux/meta/metaActions";
import { emitLoginEvent } from "@/hooks/useOnLogin";
import { logOverwolfUserEvent } from "@/utils/logOverwolfUserEvent";
import { OWUserSessionEventType, Token, UserType } from "@devour/client";
import { useControlOauthFlow } from "@/hooks/useControlOauthFlow";

interface _Props {
    checkOverwolf?: boolean;
}

export const getAuthorizedExternalOrigin = (origin: string | null): string | undefined => {
    const authorizedExternalOrigins = (import.meta.env.VITE_AUTHORIZED_EXTERNAL_ORIGINS || "http://local.external.test,https://polaris-cod.gg,https://www.polaris-cod.gg,https://devourplay.gg,https://www.devourplay.gg,https://devourgo.io,https://www.devourgo.io").split(",").map(o => o.trim());
    if (!origin) {
        return undefined;
    }
    try {
        const parsedOrigin = new URL(origin);
        return authorizedExternalOrigins
            .map(o => {
                try {
                    return new URL(o);
                } catch {
                    return null;
                }
            })
            .filter(Boolean)
            .find(url => url.hostname === parsedOrigin.hostname)?.origin;
    } catch {
        return undefined;
    }
};

function AuthLoginRedirect(_props: _Props): ReactElement {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const authToken = searchParams.get("token");
    const tokenId = searchParams.get("id");
    const stateParam = searchParams.get("state");
    const { onReauthorize } = useOnAuthThirdweb();
    const { isOnOverwolf } = useOverwolfInterop();
    const localOAuthClientId = window.localStorage?.getItem("oauthClientId") ?? null;
    const { isOauthFlowActive, navigateToOauthCallback, oAuthClient } = useControlOauthFlow(localOAuthClientId);
    const navigate = useNavigate();

    useEffect(() => {
        void onNewLoginComplete();
    }, [authToken]);

    async function onNewLoginComplete(): Promise<void> {
        if (!authToken) {
            navigate("/");
            return;
        }
        try {
            const token: Token = {
                id: tokenId || "",
                type: UserType.STANDARD_USER,
                token: authToken,
                updatedAt: 0,
                createdAt: 0,
            };
            await onReauthorize(token, true);

            emitLoginEvent();
            if (isOnOverwolf) {
                void logOverwolfUserEvent(OWUserSessionEventType.USER_LOGIN);
            }
            if (isOauthFlowActive() && oAuthClient) {
                navigateToOauthCallback();
                return;
            }
            // Check if state parameter contains a redirect URL (e.g., from Blitz OAuth flow)
            if (stateParam) {
                try {
                    const stateUrl = new URL(stateParam);
                    // Only allow redirects to our own origin and external paths
                    if (stateUrl.origin === import.meta.env.VITE_WEB_URL && stateUrl.pathname.startsWith("/external/")) {
                        window.location.href = stateParam;
                        return;
                    }
                } catch {
                    // Invalid URL in state, continue with normal flow
                }
            }
            const authorizedExternalOrigin = getAuthorizedExternalOrigin(window?.opener?.origin || document?.referrer);


            const buid = searchParams.get("buid");

            if (window?.opener?.origin === import.meta.env.VITE_WEB_URL) {
                // If this is in a popup window, notify origin window and close this one.
                if (buid) {
                    window.location.href = `/external/oauth/consent?buid=${encodeURIComponent(buid)}`;
                    return;
                }

                window.opener.postMessage({
                    type: "devourgo-social-login",
                    authToken: token,
                }, import.meta.env.VITE_WEB_URL);
                window.close();
            } else if (window?.opener?.origin === authorizedExternalOrigin && buid) {
                window.location.href = `/external/oauth/consent?buid=${encodeURIComponent(buid)}`;

            } else if (authorizedExternalOrigin) {
                /*
                 * If this is in a popup window, and the frontend is hosted on an external host
                 * No buid - notify origin window and close
                 */
                window.opener.postMessage({
                    type: "devourgo-social-login",
                    authToken: token,
                }, authorizedExternalOrigin);
                window.close();
            } else {
                // Electron users go to /play with full page reload to reset React Query cache
                const isElectron = Boolean((window as any).electronAPI);
                if (isElectron) {
                    window.location.href = "/play";
                } else {
                    navigate("/");
                }
            }
        } catch (err) {
            dispatch(await addError(err));
        }

    }

    return null;

}

export default AuthLoginRedirect;
